<template>
    <div>
        <div class="elevation-0 compact-toolbar overflow-auto">
            <KpTooltip v-if="mode === 'normal'">
                <template #activator="{ on }">
                    <ActionBtn depressed small color="accent" v-on="on" @click="onNewEventBtnClick">
                        <KpIcon icon-cls="new" color="white" />
                    </ActionBtn>
                </template>
                {{ $t('neu_event') }}
            </KpTooltip>
            <ActionBtn v-if="mode === 'new-event'" depressed small color="accent" @click="onCancelNewEventBtnClick">
                <KpIcon icon-cls="x" color="white" />
            </ActionBtn>
            <span class="mx-1">|</span>
            <IconBtn
                btn-class="kp-size--small"
                icon-cls="rotate"
                icon-color="white"
                :tooltip="String($t('reset_search'))"
                margin-class="ma-0"
                @click="onResetBtnClick"
            />
            <span>|</span>
            <div class="d-flex align-center flex-shrink-0">
                <StatusToggleBtns
                    :value="value.event_status"
                    btns-margin-class="ma-0 kp-size--small"
                    multiple
                    @change="onStatusChange"
                />
                <v-btn-toggle :value="value.own_staff" background-color="primary" @change="onOwnStaffChange">
                    <IconBtn
                        :value="true"
                        :tooltip="String($t('own'))"
                        btn-class="kp-size--small"
                        icon-cls="person"
                        icon-color="white"
                        icon-disabled-color="black"
                        margin-class="ma-0"
                    />
                </v-btn-toggle>
            </div>
            <span>|</span>
            <!-- redigiert / editorially_edited -->
            <v-btn-toggle :value="value.edited" background-color="primary" @change="onEditedChange">
                <IconBtn
                    :value="true"
                    btn-class="kp-size--small"
                    icon-cls="editedafter"
                    icon-color="white"
                    icon-disabled-color="black"
                    :tooltip="String($t('redigiert'))"
                    margin-class="ma-0"
                />
                <IconBtn
                    :value="false"
                    btn-class="kp-size--small"
                    icon-cls="not_editedafter"
                    icon-color="white"
                    icon-disabled-color="black"
                    :tooltip="String($t('not_redigiert'))"
                    margin-class="ma-0"
                />
            </v-btn-toggle>

            <span class="me-1">|</span>

            <div class="d-flex align-center flex-shrink-0" style="gap: 3px">
                <DateTextfield
                    ref="fromTextField"
                    :value="value.schedule_date_from || undefined"
                    :placeholder="`${$t('date')} ${$t('von')}`"
                    textfield-style="width: 150px"
                    hide-details
                    @input="onDateFromChange"
                />
                <DateTextfield
                    :value="value.schedule_date_to || undefined"
                    :placeholder="`${$t('date')} ${$t('bis')}`"
                    textfield-style="width: 150px"
                    hide-details
                    @input="onDateToChange"
                />
            </div>

            <span class="mx-1">|</span>

            <div class="d-flex align-center flex-shrink-0" style="gap: 3px">
                <v-text-field
                    :value="value.ort"
                    append-icon="ic ic-20 ic-searchmagnifier ic-col-black"
                    :placeholder="String($t('ort'))"
                    dense
                    outlined
                    hide-details
                    @change="onCityChange"
                />
            </div>

            <span class="mx-1">|</span>

            <div class="d-flex align-center flex-shrink-0" style="gap: 3px">
                <AutocompleteDisplay
                    :collection="locationCollection"
                    item-text="titleWithLocation"
                    item-value="id"
                    :placeholder="String($t('location_search'))"
                    :display-value="locationSelected?.titleWithLocation"
                    display-hide-details="auto"
                    query-param="simpleQuery"
                    clearable
                    @selected="onLocationSelected"
                >
                    <template #item="{ item, on, attrs }">
                        <AddressComboListItem :item="item" v-bind="attrs" v-on="on" />
                    </template>
                </AutocompleteDisplay>
                <v-text-field
                    class="ml-1"
                    :value="value.location_text"
                    append-icon="ic ic-20 ic-searchmagnifier ic-col-black"
                    :placeholder="String($t('location_freitext_search'))"
                    dense
                    outlined
                    hide-details
                    @change="onLocationTextChange"
                />
            </div>

            <span class="mx-1">|</span>

            <div class="d-flex align-center flex-shrink-0" style="gap: 3px">
                <GenreGroupSelect
                    :value="value.genre_group_ids"
                    :page-id="1"
                    :placeholder="String($t('genre'))"
                    multiple
                    dense
                    hide-details
                    @change="onGenreGroupChange"
                />

                <EventClassSelect
                    :value="value.event_class"
                    hide-details
                    :placeholder="String($t('veranstaltungsart'))"
                    @change="onEventClassChange"
                />
            </div>

            <span class="mx-1">|</span>

            <div class="d-flex align-center flex-shrink-0" style="gap: 3px">
                <v-text-field
                    :value="value.title"
                    append-icon="ic ic-20 ic-searchmagnifier ic-col-black"
                    dense
                    :placeholder="String($t('title'))"
                    outlined
                    hide-details
                    @change="onTitleChange"
                />
            </div>

            <span class="mx-1">|</span>

            <div class="d-flex align-center flex-shrink-0" style="gap: 3px">
                <AutocompleteDisplay
                    :collection="eoCollection"
                    item-text="displayTitle"
                    item-value="id"
                    :placeholder="String($t('event_organizer'))"
                    :display-value="eoSelected?.displayTitle"
                    display-hide-details="auto"
                    query-param="simpleQuery"
                    clearable
                    @selected="onEoSelected"
                >
                    <template #item="{ item, on, attrs }">
                        <AddressComboListItem :item="item" v-bind="attrs" v-on="on" />
                    </template>
                </AutocompleteDisplay>
            </div>

            <span class="mx-1">|</span>
            <div class="d-flex align-center flex-shrink-0" style="gap: 3px">
                <TrueFalseCombo
                    :value="value.for_print"
                    nullable
                    :placeholder="String($t('print'))"
                    @change="onPrintChange"
                />
                <TrueFalseCombo
                    :value="value.for_website"
                    nullable
                    :placeholder="String($t('website'))"
                    @change="onWebsiteChange"
                />
                <TrueFalseCombo
                    :value="value.cancelled"
                    nullable
                    :placeholder="String($t('cancelled'))"
                    @change="onCancelledChange"
                />
                <TrueFalseCombo
                    :value="value.is_featured"
                    :placeholder="String($t('tip'))"
                    nullable
                    @change="onTipChange"
                />
            </div>

            <span class="mx-1">|</span>
            <div class="d-flex align-center flex-shrink-0" style="gap: 3px">
                <DataProviderSelect
                    :value="value.bp_import_uuid"
                    :placeholder="String($t('quelle'))"
                    multiple
                    hide-details
                    @change="onDataProviderChange"
                />
            </div>
        </div>

        <v-form v-if="mode === 'new-event'" v-model="newEventFormValid" class="new-event-panel">
            <v-container fluid class="pa-0">
                <v-row dense style="max-width: 800px">
                    <!-- 1st column of new event panel-->
                    <v-col cols="12" md="6">
                        <v-row dense>
                            <v-col>
                                <LabelField :label="$t('date_from_to') + '*'">
                                    <div class="d-flex gap-5">
                                        <DateTextfield
                                            :value="value.schedule_date_from || undefined"
                                            :placeholder="`${$t('select_date')}`"
                                            :rules="[formRules['required']]"
                                            :hide-details="false"
                                            autofocus
                                            tabindex="10"
                                            @input="onDateFromChange"
                                        />
                                        <DateTextfield
                                            :value="value.schedule_date_to || undefined"
                                            :placeholder="`${$t('select_date')}`"
                                            :hide-details="false"
                                            :value-from="
                                                value.schedule_date_from?.isValid()
                                                    ? value.schedule_date_from.add(1, 'day').format('YYYY-MM-DD')
                                                    : undefined
                                            "
                                            tabindex="11"
                                            @input="onDateToChange"
                                        />
                                    </div>
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <LabelField :label="$t('location') + '*'">
                                    <LocationSelectField
                                        :location-id="value.location_id || undefined"
                                        :placeholder="String($t('placeholder_event_info_categories_location_id'))"
                                        :show-edit="false"
                                        :show-new="false"
                                        :show-icon-bar="false"
                                        :city-filter="value.ort || ''"
                                        :rules="[formRules['required']]"
                                        :sorter="{ property: 'title', direction: 'ASC' }"
                                        tabindex="14"
                                        @selected="onLocationSelected"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <LabelField :label="$t('title') + '*'">
                                    <v-text-field
                                        v-model="newEventData.title"
                                        dense
                                        hide-details="auto"
                                        outlined
                                        clearable
                                        tabindex="15"
                                        :counter="globalParam('event.max_title_chars', 70)"
                                        :rules="[
                                            formRules.required,
                                            formRules.maxLength(globalParam('event.max_title_chars', 70)),
                                        ]"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <LabelField :label="$t('description_short') + '*'">
                                    <v-textarea
                                        v-model="newEventData.summary_text"
                                        rows="4"
                                        dense
                                        hide-details="auto"
                                        outlined
                                        clearable
                                        tabindex="18"
                                        :counter="globalParam('event.max_summary_text_chars', 200)"
                                        :rules="[formRules.eventSummaryText]"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                    </v-col>
                    <!-- 2nd column of new event panel-->
                    <v-col cols="12" md="6">
                        <v-row dense>
                            <v-col>
                                <LabelField :label="$t('start')">
                                    <TimeTextfield
                                        v-model="newEventData.event_time_start"
                                        :placeholder="String($t('time_placeholder'))"
                                        hide-details="auto"
                                        tabindex="12"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <LabelField :label="`${$t('ort')} (${$t('search')})`">
                                    <v-text-field
                                        :value="value.ort"
                                        :placeholder="$t('search')"
                                        dense
                                        hide-details="auto"
                                        outlined
                                        clearable
                                        tabindex="13"
                                        @change="onCityChange"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <LabelField :label="$t('event_organizer')">
                                    <AutocompleteDisplay
                                        :collection="eoCollection"
                                        item-text="displayTitle"
                                        :placeholder="String($t('placeholder_event_info_categories_organizer_id'))"
                                        :display-value="eoSelected?.displayTitle"
                                        :rules="eoMandatory ? [formRules.required] : []"
                                        display-hide-details="auto"
                                        query-param="simpleQuery"
                                        clearable
                                        @selected="onEoSelected"
                                    >
                                        <template #item="{ item, on, attrs }">
                                            <AddressComboListItem :item="item" v-bind="attrs" v-on="on" />
                                        </template>
                                    </AutocompleteDisplay>
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <LabelField :label="$t('webseite')">
                                    <v-text-field
                                        v-model="newEventData.url"
                                        dense
                                        hide-details="auto"
                                        outlined
                                        clearable
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <LabelField :label="$t('hauptsparte')">
                                    <GenreGroupSelect
                                        v-model="newEventData.main_genre_group_id"
                                        :page-id="1"
                                        :placeholder="$t('placeholder_event_info_categories_main_genre_group_id')"
                                        clearable
                                        tabindex="16"
                                        hide-details="auto"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <LabelField :label="$t('veranstaltungsart')">
                                    <EventClassSelect v-model="newEventData.event_class" />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <LabelField :label="$t('description_short_2')">
                                    <v-textarea
                                        v-model="newEventData.abstract"
                                        rows="2"
                                        dense
                                        hide-details="auto"
                                        outlined
                                        clearable
                                        counter="60"
                                        tabindex="17"
                                        :rules="[formRules.maxLength(60)]"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" class="d-flex justify-end gap-5">
                                <ActionBtn
                                    color="accent"
                                    :disabled="!newEventFormValid"
                                    :loading="loading"
                                    class="new-event-btn"
                                    @click="createNewEventBtn"
                                >
                                    <KpIcon icon-cls="save" color="white" />
                                    {{ $t('save') }}
                                </ActionBtn>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-container>
        </v-form>
    </div>
</template>

<script setup lang="ts">
import { Location, LocationCollection } from '@/models/Location';
import { Dayjs } from 'dayjs';
import { Ref, onMounted, reactive, ref, watchEffect } from 'vue';
import AddressComboListItem from '../address/AddressComboListItem.vue';
import GenreGroupSelect from '@/components/genre_group/select.vue';
import DataProviderSelect from '@/components/data_provider/Select.vue';
import TrueFalseCombo from '@/components/TrueFalseCombo.vue';
import { EventOrganizer, EventOrganizerCollection } from '@/models/EventOrganizer';
import StatusToggleBtns from './StatusToggleBtns.vue';
import ActionBtn from '../buttons/ActionBtn.vue';
import KpTooltip from '../KpTooltip.vue';
import LabelField from '../form/LabelField.vue';
import { errToStr, fieldRuleValue, formRules } from '@/helpers/tools';
import TimeTextfield from '../form/TimeTextfield.vue';
import LocationSelectField from '@/components/location/SelectField.vue';
import { commitToast } from '@/plugins/toast';
import { instance as api } from '@/helpers/api';
import { Event } from '@/models/Event';
import { globalParam } from '@/helpers/globals';
import AutocompleteDisplay from '../form/AutocompleteDisplay.vue';
import IconBtn from '../IconBtn.vue';
import DateTextfield from '../form/DateTextfield.vue';
import EventClassSelect from './EventClassSelect.vue';

const emit = defineEmits(['input', 'new-event', 'reset']);

type SearchParams = {
    event_status?: string[];
    schedule_date_from?: Dayjs | null;
    schedule_date_to?: Dayjs | null;
    ort?: string | null;
    location_id?: number | null;
    location_text?: string | null;
    event_organizer_id?: number | null;
    genre_group_ids?: Array<number> | null;
    event_class: string;
    title?: string | null;
    for_print?: boolean | null;
    for_website?: boolean | null;
    is_featured?: boolean | null;
    edited?: boolean | null;
    cancelled?: boolean | null;
    bp_import_uuid?: Array<string> | null;
    own_staff?: boolean | null;
};
const initialSearchParams: SearchParams = {
    event_status: [],
    schedule_date_from: null,
    schedule_date_to: null,
    ort: null,
    location_id: null,
    location_text: null,
    event_organizer_id: null,
    genre_group_ids: [],
    event_class: '',
    title: null,
    for_print: null,
    is_featured: null,
    cancelled: false,
    bp_import_uuid: [],
    edited: null,
    own_staff: false,
};

const props = defineProps<{
    value: SearchParams;
}>();

const newEventFormValid = ref(true);
const mode = ref<'normal' | 'new-event'>('normal');
const loading = ref(false);

const newEventData = reactive({
    start_date: null as Dayjs | null,
    end_date: null as Dayjs | null,
    event_time_start: undefined as string | undefined,
    location_id: undefined as number | undefined,
    main_genre_group_id: null as number | null,
    title: null as string | null,
    url: null as string | null,
    abstract: null as string | null,
    summary_text: null as string | null,
    // we set the print and website flag by default, see #27672:
    for_print: true,
    for_website: true,
    event_class: Event.CLASS_EVENT,
});

const locationSelected: Ref<Location | null> = ref(null);
const locationCollection = new LocationCollection();
locationCollection.addExtraParam('showAllLocations', true);

const eoSelected: Ref<EventOrganizer | null> = ref(null);
const eoCollection = new EventOrganizerCollection();
const eoMandatory = fieldRuleValue('field_rules.mandatory.event.organizer_id', false);
const fromTextField: Ref<HTMLElement | null> = ref(null);

watchEffect(() => {
    newEventData.start_date = props.value.schedule_date_from as any;
    newEventData.end_date = props.value.schedule_date_to as any;
    newEventData.location_id = props.value.location_id || undefined;
    if (!props.value.location_id) {
        locationSelected.value = null;
    }
});

onMounted(async () => {
    if (props.value?.location_id) {
        await locationCollection.query({
            filter: JSON.stringify({ property: 'id', value: props.value.location_id }),
        });
        locationSelected.value = (locationCollection.first() as Location | undefined) || null;
    }
    if (fromTextField.value) {
        fromTextField.value.focus();
    }
});

function onNewEventBtnClick() {
    // reset all search params when the new event panel is shown:
    emit('input', { ...initialSearchParams });
    mode.value = 'new-event';
}
function onCancelNewEventBtnClick() {
    mode.value = 'normal';
}
function onStatusChange(val: string | null) {
    emit('input', { ...props.value, ...{ event_status: val } });
}
function onDateFromChange(val: Dayjs) {
    emit('input', { ...props.value, ...{ schedule_date_from: val } });
}
function onDateToChange(val: Dayjs) {
    emit('input', { ...props.value, ...{ schedule_date_to: val } });
}
function onCityChange(val: string | null) {
    emit('input', { ...props.value, ...{ ort: val } });
}
function onLocationSelected(val: Location | null) {
    locationSelected.value = val;
    emit('input', { ...props.value, ...{ location_id: val?.id || null } });
}
function onLocationTextChange(val: string | null) {
    emit('input', { ...props.value, ...{ location_text: val || null } });
}
function onEoSelected(val: EventOrganizer | null) {
    eoSelected.value = val;
    emit('input', { ...props.value, ...{ event_organizer_id: val?.id || null } });
}
function onGenreGroupChange(val: Array<number> | null) {
    emit('input', { ...props.value, ...{ genre_group_ids: val || [] } });
}
function onEventClassChange(val: string) {
    emit('input', { ...props.value, ...{ event_class: val || '' } });
}
function onTitleChange(val: string | null) {
    emit('input', { ...props.value, ...{ title: val } });
}
function onPrintChange(val: boolean | null) {
    emit('input', { ...props.value, ...{ for_print: val } });
}
function onWebsiteChange(val: boolean | null) {
    emit('input', { ...props.value, ...{ for_website: val } });
}
function onTipChange(val: boolean | null) {
    emit('input', { ...props.value, ...{ is_featured: val } });
}
function onEditedChange(val: boolean | null) {
    emit('input', { ...props.value, ...{ edited: val } });
}
function onCancelledChange(val: boolean | null) {
    emit('input', { ...props.value, ...{ cancelled: val } });
}
function onOwnStaffChange(val: boolean | null) {
    emit('input', { ...props.value, ...{ own_staff: val } });
}

function onResetBtnClick() {
    if (fromTextField.value) {
        fromTextField.value.focus();
    }
    emit('reset');
}

function onDataProviderChange(val: string | null) {
    emit('input', { ...props.value, ...{ bp_import_uuid: val ?? [] } });
}

async function createNewEventBtn() {
    if (newEventFormValid.value) {
        loading.value = true;
        try {
            const res = await api().post('/Event/quickCreate', newEventData);
            const eventData = res?.data?.event;
            // reset input form selectively:
            newEventData.title = null;
            newEventData.abstract = null;
            newEventData.summary_text = null;
            newEventData.url = null;
            newEventData.event_class = Event.CLASS_EVENT;
            newEventData.main_genre_group_id = null;
            // close edit form:
            mode.value = 'normal';
            // emit new event
            emit('new-event', new Event(eventData));
        } catch (e) {
            commitToast({ message: errToStr(e), color: 'error' });
        } finally {
            loading.value = false;
        }
    }
}
</script>

<style lang="scss" scoped>
.compact-toolbar {
    $iconSize: 24px;

    display: flex;
    gap: 2px;
    padding: 3px 5px 3px 5px;
    background-color: var(--primary-color);
    color: white;
    align-items: center;
    :deep(.v-text-field) {
        background-color: white;
        margin: 0;
    }

    :deep(.v-input--checkbox) {
        .v-icon {
            background-color: white;
        }
    }
    :deep(.kp-size--small) {
        width: $iconSize;
        height: $iconSize;
    }
}
.new-event-panel {
    padding: 4px;
    color: white;
    border-bottom: 2px solid var(--gray-border-color);
}

.status-group {
    &.v-input--selection-controls {
        margin-top: 0;
    }
    :deep(.v-radio) {
        margin-right: 0;
    }
    .status-draft {
        :deep(.v-icon.v-icon) {
            background-color: var(--icon-status-draft);
        }
    }
    .status-registered {
        :deep(.v-icon.v-icon) {
            background-color: var(--icon-status-registered);
        }
    }
    .status-released {
        :deep(.v-icon.v-icon) {
            background-color: var(--icon-status-released);
        }
    }
    .status-rejected {
        :deep(.v-icon.v-icon) {
            background-color: var(--icon-status-rejected);
        }
    }
    .status-imported {
        :deep(.v-icon.v-icon) {
            background-color: var(--icon-status-imported);
        }
    }
}

.new-event-btn {
    &.v-btn--disabled {
        background-color: unset !important;
    }
}
</style>
